import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../shared/firebase';
import { extractString } from '../../../shared/fieldUtils';
import missionArt from '../../../assets/mission_art.png';

// Custom SVG Icons matching the reference design line-art
const ParishesIcon = (props) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M12 2v3M10 3.5h4M12 5L5 10v10h14V10L12 5z" />
    <path d="M10 20v-4a2 2 0 0 1 4 0v4M12 10v3M10 11.5h4" />
  </svg>
);

const InstitutionsIcon = (props) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M12 2v2M10 3h4M12 4c-2.5 0-4 2-4 4.5V11H4v9h16v-9h-4V8.5c0-2.5-1.5-4-4-4z" />
    <path d="M10 20v-4a2 2 0 0 1 4 0v4M8 14h8" />
  </svg>
);

const FaithfulIcon = (props) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="8" cy="8" r="2" />
    <path d="M4 18a3 3 0 0 1 6 0v2H4v-2z" />
    <circle cx="16" cy="8" r="2" />
    <path d="M12 18a3 3 0 0 1 6 0v2h-6v-2z" />
    <circle cx="12" cy="13" r="1.5" />
    <path d="M9.5 20a1.5 1.5 0 0 1 3 0v1h-3v-1z" />
  </svg>
);

const YearsIcon = (props) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M12 4v16M8 9h8" />
    <path d="M7 7l2 2M17 7l-2 2M7 17l2-2M17 17l-2-2" />
  </svg>
);

export default function MissionStatsRow({ onSelectSection = () => {} }) {
  const [counts, setCounts] = useState({
    parishes: '26+',
    institutions: '120+',
    faithful: '4.5M+',
    years: '175+'
  });

  useEffect(() => {
    let isMounted = true;
    async function fetchStats() {
      try {
        const snap = await getDocs(collection(db, 'stats')).catch(() => null);
        if (snap && !snap.empty) {
          const d = snap.docs[0].data();
          if (isMounted) {
            setCounts({
              parishes: extractString(d.dioceses || d.diocese || d.parishes || d.churches, '26+'),
              institutions: extractString(d.institutions, '120+'),
              faithful: extractString(d.members || d.faithful, '4.5M+'),
              years: extractString(d.years, '175+')
            });
          }
        }
      } catch (err) {
        console.error("Error fetching stats:", err);
      }
    }
    fetchStats();
    return () => { isMounted = false; };
  }, []);

  const stats = [
    { num: counts.parishes, label: 'Dioceses', icon: ParishesIcon, route: 'churches' },
    { num: counts.institutions, label: 'Institutions', icon: InstitutionsIcon, route: 'institutions' },
    { num: counts.faithful, label: 'Members', icon: FaithfulIcon, route: 'about' },
    { num: counts.years, label: 'Years of Heritage', icon: YearsIcon, route: 'history' },
  ];

  return (
    <section className="py-1 sm:py-2 bg-transparent">
      <div className="max-w-[1440px] mx-auto px-3.5 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-12 gap-2.5 sm:gap-5">
          
          {/* Our Mission */}
          <div 
            onClick={() => onSelectSection('about')}
            className="col-span-2 sm:col-span-2 lg:col-span-4 bg-gradient-to-br from-[#fdf0f3] to-[#f9e6eb] rounded-[20px] sm:rounded-[24px] p-4 sm:p-7 border border-[#f4ced8]/70 flex items-center relative overflow-hidden min-h-[140px] sm:h-[220px] shadow-[0_4px_25px_rgba(0,0,0,0.03)] group hover:shadow-[0_8px_30px_rgba(132,35,67,0.08)] hover:-translate-y-0.5 transition-all duration-500 cursor-pointer"
          >
            <div className="absolute top-0 left-0 w-28 h-28 bg-white/60 blur-[25px] rounded-full pointer-events-none"></div>

            <div className="relative z-10 max-w-[62%] sm:max-w-[52%] pr-1 flex flex-col justify-between h-full py-0.5 sm:py-1">
              <div>
                <h2 className="font-serif font-bold text-[#842343] text-[18px] sm:text-[24px] mb-1 sm:mb-2 leading-tight">
                  Our Mission
                </h2>
                <p className="font-medium text-[#1a1715] text-[11px] sm:text-[13px] leading-[1.35] sm:leading-[1.5] mb-2 sm:mb-3 line-clamp-3">
                  To Witness, to educate, to heal and serve all with Christ's transformative love
                </p>
              </div>
              <button 
                onClick={(e) => { e.stopPropagation(); onSelectSection('about'); }}
                className="text-[#842343] text-[10.5px] sm:text-xs font-bold uppercase tracking-wider flex items-center gap-1 group/btn w-max cursor-pointer"
              >
                <span className="group-hover/btn:underline underline-offset-4 decoration-2 decoration-[#c39b5b]">Learn More</span>
                <span className="transition-transform duration-300 group-hover/btn:translate-x-1">→</span>
              </button>
            </div>
            
            <div className="absolute right-0 bottom-0 top-0 w-[42%] sm:w-[45%] flex justify-end items-end opacity-85 mix-blend-multiply pointer-events-none group-hover:scale-105 transition-transform duration-700">
              <img 
                src={missionArt} 
                alt="Mission Art" 
                className="h-[92%] w-auto max-w-full object-contain object-right-bottom p-1 sm:p-2 opacity-90 filter mix-blend-multiply" 
              />
            </div>
          </div>

          {/* Statistics separate cards */}
          {stats.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <div 
                key={idx} 
                onClick={() => onSelectSection(stat.route)}
                className="col-span-1 sm:col-span-1 lg:col-span-2 bg-white rounded-[18px] sm:rounded-[24px] border border-gray-100 p-3 sm:p-6 shadow-[0_4px_20px_rgba(0,0,0,0.02)] flex flex-col items-center justify-center text-center hover:shadow-[0_8px_30px_rgba(0,0,0,0.05)] hover:-translate-y-0.5 transition-all duration-300 group cursor-pointer min-h-[110px] sm:h-[220px]"
              >
                <Icon className="w-6 h-6 sm:w-10 sm:h-10 text-[#c39b5b] mb-1.5 sm:mb-3.5 group-hover:scale-110 transition-transform duration-300 shrink-0" />
                <span className="font-serif font-bold text-[22px] sm:text-[30px] text-[#1a1715] leading-none mb-1 sm:mb-2 group-hover:text-[#842343] transition-colors">
                  {stat.num}
                </span>
                <span className="font-sans font-bold text-[9.5px] sm:text-[11px] uppercase tracking-[0.04em] sm:tracking-[0.08em] text-[#8a7e79] leading-tight px-0.5">
                  {stat.label}
                </span>
              </div>
            );
          })}

        </div>
      </div>
    </section>
  );
}
